import { useState, useRef, useEffect } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Box,
  Button,
  LinearProgress,
  LinearProgressProps,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { apiError } from '@bumble/app/utils/api-error';
import { toast } from 'react-toastify';
import { NetworkInterfaceSelect, NetworkInterfaceValue } from '@bumble/app/components/NetworkInterfaceSelect';

interface ConnectConsoleAccessProps {
  onSubmit: () => void;
  onReady: (url: string) => void;
  cpeID: string;
  hwIDPath: string;
  open: boolean;
  // The interface this USB device/session is already pinned to, per
  // get-cpe-usb-devices' `interface` field — null/undefined means it hasn't
  // been pinned yet and the user can pick one for this connection.
  interfaceValue?: string | null;
}

export const ConnectConsoleAccess = ({
  onSubmit, onReady, cpeID, hwIDPath, open, interfaceValue,
}: ConnectConsoleAccessProps) => {
  const api = useApi();

  const isLocked = !!interfaceValue;
  const [selectedInterface, setSelectedInterface] = useState<NetworkInterfaceValue>(
    (interfaceValue as NetworkInterfaceValue) || 'default'
  );
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  // Reset the selection each time the dialog is opened for a (possibly
  // different) device.
  useEffect(() => {
    if (open) {
      setSelectedInterface((interfaceValue as NetworkInterfaceValue) || 'default');
    }
  }, [open, interfaceValue]);

  function UploadProgressWithLabel(
    props: LinearProgressProps & { value: number }
  ) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5 }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {`${Math.round(props.value)}%`}
          </Typography>
        </Box>
      </Box>
    );
  }

  const connectConsoleAccess = useMutation({
    mutationFn: async () => {
      controllerRef.current = new AbortController();
      setShowProgress(true);
      setProgress(0);
      let value = 0;
      const MAX_PROGRESS = 99;

      timerRef.current = setInterval(() => {
        value += 1;

        if (value >= MAX_PROGRESS) {
          value = MAX_PROGRESS;
          setProgress(value);
          clearInterval(timerRef.current!);
          return;
        }

        setProgress(value);
      }, 1200);

      return api
        .connectConsoleAccessApiV1ConnectConsoleAccessPost({
            cpe_id: cpeID,
            hw_id_path: hwIDPath,
            console_mode: 'usb',
            interface: selectedInterface,
          },
          { signal: controllerRef.current.signal }
        )
        .finally(() => {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          setShowProgress(false);
        });
    },
    onSuccess(res: any) {
      toast.success('Connected Console Access successfully.');
      setProgress(100);
      const url = res?.data?.web_url ?? '';
      onSubmit();
      onReady(url);
    },
    onError(error: any) {
      if (error?.name === 'AbortError') return;
      if (timerRef.current) clearInterval(timerRef.current);
      setShowProgress(false);
      setProgress(0);
      apiError(error);
    },
  });

  useEffect(() => {
    if (!open) {
      if (controllerRef.current) controllerRef.current.abort();
      if (timerRef.current) clearInterval(timerRef.current);
      setShowProgress(false);
      setProgress(0);
    }

    return () => {
      if (controllerRef.current) controllerRef.current.abort();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open]);

  const handleConnect = () => {
    connectConsoleAccess.mutate();
  };

  return (
    <Box pt={2}>
      {showProgress ? (
        <Box sx={{ width: '100%', mt: 2 }}>
          <UploadProgressWithLabel
            color="info"
            sx={{ height: 10 }}
            value={progress}
          />
        </Box>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Select the network interface to use for this OOB console connection.
          </Typography>
          <NetworkInterfaceSelect
            value={selectedInterface}
            onChange={setSelectedInterface}
            locked={isLocked}
            lockedMessage="This device's console session is already pinned to this interface."
          />
          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={handleConnect} disabled={connectConsoleAccess.isLoading}>
              Connect
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
